// src/components/pages/Pessoa/modules/usePessoaModule.js

import { useEffect, useMemo, useState, useCallback } from 'react';
import { resolvePessoaProfile } from '../profile/resolvePessoaProfile';

import * as PessoaAPI from '../../../../api/pessoa';
import * as FinanceiroAPI from '../../../../api/financeiro';
import * as TabelaAPI from '../../../../api/tabela';
import * as DataListAPI from '../../../../api/dataList';
import * as VerificadoresAPI from '../../../../api/verificadores';

import { getPessoaBoot } from '../boot/getPessoaBoot';
import { onlyNumbers, isCpf, isCnpj, getTipoByDocumento } from '../helpers/pessoaDocUtils';
import { buildPessoaReceitaData } from '../helpers/buildPessoaReceitaData';
import { applyPessoaExistente } from '../helpers/applyPessoaExistente';

import { criarUsuarioDraft } from '../helpers/criarUsuarioDraft';

export function usePessoaModule({ formData, tipo, btnText, empresaIds = [], navigate, params, setFlashMessage }) {
    const profile = useMemo(() => resolvePessoaProfile(tipo), [tipo]);

    const [dados, setDados] = useState({});
    const [pessoaFisica, setPessoaFisica] = useState({});
    const [pessoaJuridica, setPessoaJuridica] = useState({});
    const [pessoaUsuario, setPessoaUsuario] = useState(criarUsuarioDraft());
    const [pessoaColaborador, setPessoaColaborador] = useState({});

    const [pessoaEnderecos, setPessoaEndereco] = useState([]);
    const [pessoaEmails, setPessoaEmail] = useState([]);
    const [pessoaTelefones, setPessoaTelefone] = useState([]);
    const [pessoaNomes, setPessoaNome] = useState([]);
    const [pessoaFuncionarios, setPessoaFuncionarios] = useState([]);
    const [pessoaTabelaPrecos, setPessoaTabelaPrecos] = useState([]);
    const [pessoaEmpresas, setPessoaEmpresas] = useState([]);

    const [optionsFormaCliente, setOptionsFormaCliente] = useState([]);
    const [optionsFormaFornecedor, setOptionsFormaFornecedor] = useState([]);
    const [optionsConta, setOptionsConta] = useState([]);
    const [optionsPCliente, setOptionsPCliente] = useState([]);
    const [optionsPFornecedor, setOptionsPFornecedor] = useState([]);

    const [centroCustoCliente, setCentroCustoCliente] = useState({ descricao: '' });
    const [centroCustoFornecedor, setCentroCustoFornecedor] = useState({ descricao: '' });
    const [optionsCCC, setOptionsCCC] = useState([]);
    const [optionsCCF, setOptionsCCF] = useState([]);

    const flash = useCallback(
        (message, type = 'info') => {
            if (typeof setFlashMessage === 'function') {
                setFlashMessage(message, type);
            }
        },
        [setFlashMessage]
    );

    const consultaForma = useCallback(async (obj) => {
        try {
            const result = await FinanceiroAPI.buscaForma(obj);

            if (obj.tipo === 'Pagar') {
                setOptionsFormaFornecedor(result || []);
            } else if (obj.tipo === 'Receber') {
                setOptionsFormaCliente(result || []);
            } else {
                setOptionsFormaFornecedor(result || []);
                setOptionsFormaCliente(result || []);
            }
        } catch (error) {
            console.error('Erro ao consultar formas:', error);

            if (obj.tipo === 'Pagar') {
                setOptionsFormaFornecedor([]);
            } else if (obj.tipo === 'Receber') {
                setOptionsFormaCliente([]);
            } else {
                setOptionsFormaFornecedor([]);
                setOptionsFormaCliente([]);
            }
        }
    }, []);

    const buscaConta = useCallback(async (empresaId) => {
        if (!empresaId) {
            setOptionsConta([]);
            return;
        }

        try {
            const result = await FinanceiroAPI.buscaContaByEmpresaIds({ empresaIds: [empresaId] });
            setOptionsConta(result || []);
        } catch (error) {
            console.error('Erro ao buscar contas:', error);
            setOptionsConta([]);
        }
    }, []);

    const consultaPrazo = useCallback(async (tipoPrazo, formaId, empresaId) => {
        if (!formaId || !empresaId) {
            if (tipoPrazo === 'Pagar') {
                setOptionsPFornecedor([]);
            } else {
                setOptionsPCliente([]);
            }
            return;
        }

        try {
            const result = await FinanceiroAPI.prazo(tipoPrazo, formaId, '', '', empresaId, '');

            if (tipoPrazo === 'Pagar') {
                setOptionsPFornecedor(result || []);
            } else {
                setOptionsPCliente(result || []);
            }
        } catch (error) {
            console.error(`Erro ao consultar prazo ${tipoPrazo}:`, error);

            if (tipoPrazo === 'Pagar') {
                setOptionsPFornecedor([]);
            } else {
                setOptionsPCliente([]);
            }
        }
    }, []);

    const consultaDatalist = useCallback(async (payload) => {
        try {
            return await DataListAPI.dataList(payload);
        } catch (error) {
            console.error('Erro ao consultar DataList:', error);
            return [];
        }
    }, []);

    const handleFinanceiroChange = useCallback((e) => {
        const { name, value, checked, type } = e.target;

        setDados((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    }, []);

    const handleCentroCustoClienteChange = useCallback(
        async (e) => {
            const value = e.target.value;

            setCentroCustoCliente({ descricao: value });

            const result = await consultaDatalist({
                empresaId: dados?.empresaId,
                tabela: 'centroCusto',
                condicao: 'Receber',
                filtro: value,
            });

            setOptionsCCC(result || []);

            const selectedId = document.querySelector(`datalist option[value="${value}"]`)?.dataset.id;

            setDados((prev) => ({
                ...prev,
                centroCustoClienteId: selectedId ?? null,
            }));
        },
        [consultaDatalist, dados?.empresaId]
    );

    const handleCentroCustoFornecedorChange = useCallback(
        async (e) => {
            const value = e.target.value;

            setCentroCustoFornecedor({ descricao: value });

            const result = await consultaDatalist({
                empresaId: dados?.empresaId,
                tabela: 'centroCusto',
                condicao: 'Pagar',
                filtro: value,
            });

            setOptionsCCF(result || []);

            const selectedId = document.querySelector(`datalist option[value="${value}"]`)?.dataset.id;

            setDados((prev) => ({
                ...prev,
                centroCustoFornecedorId: selectedId ?? null,
            }));
        },
        [consultaDatalist, dados?.empresaId]
    );

    const handlePessoaChange = useCallback((e) => {
        const { name, value, checked, type, options, selectedIndex } = e.target;

        if (name === 'tipo') {
            setDados((prev) => ({
                ...prev,
                tipo: options?.[selectedIndex]?.text || value,
            }));
            return;
        }

        if (name === 'vinculoFinanceiro') {
            setDados((prev) => ({
                ...prev,
                vinculoFinanceiro: options?.[selectedIndex]?.text || value,
            }));
            return;
        }

        setDados((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    }, []);

    const aplicarCadastroBaseExistente = useCallback((result) => {
        applyPessoaExistente({
            result,
            setDados,
            setPessoaFisica,
            setPessoaJuridica,
            setPessoaEndereco,
            setPessoaEmail,
            setPessoaTelefone,
            setPessoaNome,
        });
    }, []);

    const consultarPessoaExistente = useCallback(
        async (doc, sistema) => {
            try {
                return await PessoaAPI.obterPessoa({
                    CPForCNPJ: doc,
                    empresaIds,
                    sistema: sistema ?? false,
                });
            } catch (error) {
                console.error('Erro ao consultar pessoa existente:', error);
                return null;
            }
        },
        [empresaIds]
    );

    const consultarCnpj = useCallback(
        async (cnpj) => {
            const cnpjLimpo = onlyNumbers(cnpj);
            if (!isCnpj(cnpjLimpo)) return null;

            try {
                const result = await VerificadoresAPI.cnpj(cnpjLimpo);

                if (!result || result.status === 'ERROR') {
                    console.error(result?.message || 'Erro ao consultar CNPJ');
                    return null;
                }

                const receitaData = buildPessoaReceitaData(result, dados?.id);

                setDados((prev) => ({
                    ...prev,
                    descricao: result.nome || prev.nomePrincipal || '',
                    nomePrincipal: result.nome || prev.nomePrincipal || '',
                    razaoSocial: result.nome || prev.nomePrincipal || '',
                    nomeFantasia: result.nome || prev.nomePrincipal || '',
                    tipo: 'Jurídica',
                    vinculoFinanceiro: prev.vinculoFinanceiro || 'Ambos',
                }));

                setPessoaJuridica((prev) => ({
                    ...prev,
                    pessoaId: prev.pessoaId || dados?.id || null,
                    ...receitaData.juridica,
                }));

                setPessoaEndereco((prev) => {
                    if (prev?.length > 0) return prev;
                    return result.cep ? [receitaData.endereco] : prev;
                });

                setPessoaEmail((prev) => {
                    if (prev?.length > 0 || !receitaData.email) return prev;
                    return [receitaData.email];
                });

                setPessoaTelefone((prev) => {
                    if (prev?.length > 0 || receitaData.telefones.length === 0) return prev;
                    return receitaData.telefones;
                });

                return result;
            } catch (error) {
                console.error('Erro ao consultar CNPJ:', error);
                return null;
            }
        },
        [dados?.id]
    );

    const redirecionarParaEdicao = useCallback(
        (uuid) => {
            if (!uuid || typeof navigate !== 'function') return;

            const nextParams = params instanceof URLSearchParams ? new URLSearchParams(params) : new URLSearchParams();

            nextParams.set('uuid', uuid);
            nextParams.set('btnText', 'Atualizar');

            navigate('/Pessoa/CreateOrEdit', nextParams);
        },
        [navigate, params]
    );

    const handlePessoaBlur = useCallback(
        async (e) => {
            const { name, value } = e.target;

            if (name !== 'CPForCNPJ') return;
            if (String(btnText || '').toLowerCase() !== 'cadastrar') return;

            const doc = onlyNumbers(value);

            if (!doc) return;

            if (!isCpf(doc) && !isCnpj(doc)) {
                flash('CPF/CNPJ inválido!', 'warning');
                return;
            }

            const tipoDocumento = getTipoByDocumento(doc);

            if (tipoDocumento) {
                setDados((prev) => ({
                    ...prev,
                    tipo: tipoDocumento,
                }));
            }

            //verifica se o parceiro existe dentro do grupo de empresas
            const pessoaExistente = await consultarPessoaExistente(doc);

            if (pessoaExistente?.uuid && !pessoaExistente.isAdmin) {
                if (String(btnText || '').toLowerCase() === 'cadastrar') {
                    redirecionarParaEdicao(pessoaExistente.uuid);
                    return;
                }

                aplicarCadastroBaseExistente(pessoaExistente);
                return;
            }

            //aqui devemos verificar se existe no sistema como um todo
            const pessoaSistema = await consultarPessoaExistente(doc, true);

            if (pessoaSistema) {
                aplicarCadastroBaseExistente(pessoaSistema);
                return;
            }
            if (isCnpj(doc)) {
                const retornoReceita = await consultarCnpj(doc);

                if (!retornoReceita) {
                    flash('CNPJ não encontrado. Continue preenchendo manualmente.', 'info');
                }
            }
        },
        [btnText, flash, consultarPessoaExistente, aplicarCadastroBaseExistente, consultarCnpj, redirecionarParaEdicao]
    );

    useEffect(() => {
        const empresaIdBoot = Number(localStorage.getItem('empresaId')) || null;
        const hasFormData = formData && Object.keys(formData).length > 0;

        if (hasFormData) {
            setDados(formData || {});
            setPessoaFisica(formData?.pessoaFisica || {});
            setPessoaJuridica(formData?.pessoaJuridica || {});
            setPessoaUsuario(criarUsuarioDraft(formData.id, formData.pessoaUsuario, formData.usuario));
            setPessoaColaborador(formData?.pessoaColaborador || {});

            setPessoaEndereco(formData?.pessoaEnderecos || []);
            setPessoaEmail(formData?.pessoaEmails || []);
            setPessoaTelefone(formData?.pessoaTelefones || []);
            setPessoaNome(formData?.pessoaNomes || []);
            setPessoaFuncionarios(formData?.pessoaFuncionarios || []);
            setPessoaTabelaPrecos(formData?.pessoaTabelaPrecos || []);
            setPessoaEmpresas(formData?.pessoaEmpresas || []);
            return;
        }

        const boot = getPessoaBoot({
            tipo,
            empresaId: empresaIdBoot,
        });

        setDados(boot);
        setPessoaFisica({});
        setPessoaJuridica({});
        setPessoaUsuario(criarUsuarioDraft(null, {}, boot.usuario));
        setPessoaColaborador({});

        setPessoaEndereco([]);
        setPessoaEmail([]);
        setPessoaTelefone([]);
        setPessoaNome([]);
        setPessoaFuncionarios([]);
        setPessoaTabelaPrecos([]);
        setPessoaEmpresas([]);
    }, [formData, tipo]);

    useEffect(() => {
        setDados((prev) => ({
            ...prev,
            pessoaFisica,
            pessoaJuridica,
            pessoaUsuario,
            pessoaColaborador,
            pessoaEnderecos,
            pessoaEmails,
            pessoaTelefones,
            pessoaNomes,
            pessoaFuncionarios,
            pessoaTabelaPrecos,
            pessoaEmpresas,
        }));
    }, [
        pessoaFisica,
        pessoaJuridica,
        pessoaUsuario,
        pessoaColaborador,
        pessoaEnderecos,
        pessoaEmails,
        pessoaTelefones,
        pessoaNomes,
        pessoaFuncionarios,
        pessoaTabelaPrecos,
        pessoaEmpresas,
    ]);

    useEffect(() => {
        setDados((prev) => ({ ...prev, pixPadrao: dados.CPForCNPJ }));
    }, [dados.pixDefinido]);

    useEffect(() => {
        if (!dados?.empresaId) {
            setOptionsFormaCliente([]);
            setOptionsFormaFornecedor([]);
            return;
        }

        consultaForma({ tipo: 'Pagar', empresaId: dados.empresaId });
        consultaForma({ tipo: 'Receber', empresaId: dados.empresaId });
    }, [dados?.empresaId, consultaForma]);

    useEffect(() => {
        buscaConta(dados?.empresaId);
    }, [dados?.empresaId, buscaConta]);

    useEffect(() => {
        consultaPrazo('Receber', dados?.formaClienteId, dados?.empresaId);
    }, [dados?.formaClienteId, dados?.empresaId, consultaPrazo]);

    useEffect(() => {
        consultaPrazo('Pagar', dados?.formaFornecedorId, dados?.empresaId);
    }, [dados?.formaFornecedorId, dados?.empresaId, consultaPrazo]);

    useEffect(() => {
        async function carregarCentroCustosSelecionados() {
            if (dados?.empresaId && dados?.centroCustoClienteId) {
                try {
                    const result = await TabelaAPI.tabelaById(dados.empresaId, 'centroCusto', dados.centroCustoClienteId);
                    if (result) {
                        setCentroCustoCliente({ descricao: result.descricao || '' });
                        setOptionsCCC([result]);
                    }
                } catch (error) {
                    console.error(error);
                }
            } else {
                setCentroCustoCliente({ descricao: '' });
                setOptionsCCC([]);
            }

            if (dados?.empresaId && dados?.centroCustoFornecedorId) {
                try {
                    const result = await TabelaAPI.tabelaById(dados.empresaId, 'centroCusto', dados.centroCustoFornecedorId);
                    if (result) {
                        setCentroCustoFornecedor({ descricao: result.descricao || '' });
                        setOptionsCCF([result]);
                    }
                } catch (error) {
                    console.error(error);
                }
            } else {
                setCentroCustoFornecedor({ descricao: '' });
                setOptionsCCF([]);
            }
        }

        carregarCentroCustosSelecionados();
    }, [dados?.empresaId, dados?.centroCustoClienteId, dados?.centroCustoFornecedorId]);

    return {
        profile,

        dados,
        setDados,

        pessoaFisica,
        setPessoaFisica,

        pessoaJuridica,
        setPessoaJuridica,

        pessoaUsuario,
        setPessoaUsuario,

        pessoaColaborador,
        setPessoaColaborador,

        pessoaEnderecos,
        setPessoaEndereco,

        pessoaEmails,
        setPessoaEmail,

        pessoaTelefones,
        setPessoaTelefone,

        pessoaNomes,
        setPessoaNome,

        pessoaFuncionarios,
        setPessoaFuncionarios,

        pessoaTabelaPrecos,
        setPessoaTabelaPrecos,

        pessoaEmpresas,
        setPessoaEmpresas,

        optionsFormaCliente,
        optionsFormaFornecedor,
        optionsConta,
        optionsPCliente,
        optionsPFornecedor,

        centroCustoCliente,
        centroCustoFornecedor,
        optionsCCC,
        optionsCCF,

        handleFinanceiroChange,
        handleCentroCustoClienteChange,
        handleCentroCustoFornecedorChange,

        handlePessoaChange,
        handlePessoaBlur,
        consultarCnpj,
        aplicarCadastroBaseExistente,
        consultarPessoaExistente,
    };
}
